/* Copyright (c) 2020-present CloudIO Inc */

import { Button, Grid, Typography } from '@mui/material';
import { Platform } from 'componentregistry';
import { getUrl } from 'config/config';
import Icon from 'icons/Icon';
import React from 'react';
import { userAtom, UserSession } from 'root/useUser';
import { getURLQueryStringToPost } from 'utils/url';
import StackTrace from 'stacktrace-js';
import { isNull } from 'utils/isEmpty';

export async function handleError(
  user: UserSession,
  error: Error,
  filename: string,
  lineno: number,
  colno: number,
) {
  try {
    if (isNull(error) && console) {
      console.warn({ error, filename, lineno, colno });
      return;
    }
    const stacks = await StackTrace.fromError(error);
    const stacktrace = stacks.map((s) => s.toString()).join('\n  ');
    await fetch(`${getUrl()}e?${getURLQueryStringToPost(user)}`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: error.message || 'Unknown',
        filename,
        lineno,
        colno,
        stacktrace,
      }),
    });
  } catch (e) {
    if (console) {
      console.error(e);
      console.error({ error, filename, lineno, colno });
    }
  }
}

export default class ErrorBoundary extends React.Component<{
  platform: Platform;
  hideClear?: boolean;
  children: React.ReactNode;
}> {
  /* eslint-disable react/state-in-constructor */
  state: { hasError: boolean; error?: Error; message?: string } = {
    hasError: false,
  };

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error,
      message:
        (typeof error === 'string' ? error : error.message) ||
        'Unexpected error! Check the console log for more info!',
    };
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    console.error(error, errorInfo);
    const user = this.props.platform.getter(userAtom);
    if (user) {
      handleError(user, error, 'ErrorBoundary', 0, 0);
    }
  }

  reset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Grid
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
          }}
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid
            item
            container
            justifyContent="center"
            alignItems="center"
            direction="column"
          >
            <Icon
              iconName="bug"
              iconType="solid"
              color="error"
              data-tip={this.state.message}
              data-tip-onclick
              style={{ cursor: 'pointer', zIndex: 9 }}
            />
            <Typography variant="subtitle1" color="error">
              {this.state.message}
            </Typography>
            {this.props.hideClear ? null : (
              <Button variant="contained" onClick={this.reset}>
                Clear
              </Button>
            )}
          </Grid>
        </Grid>
      );
    }
    return this.props.children ?? null;
  }
}

export class ConsoleErrorBoundary extends React.Component<{
  name: string;
  platform: Platform;
  children: React.ReactNode;
}> {
  /* eslint-disable react/state-in-constructor */
  state: { hasError: boolean; error?: Error; message?: string } = {
    hasError: false,
  };

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error,
      message:
        (typeof error === 'string' ? error : error.message) ||
        'Unexpected error! Check the console log for more info!',
    };
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    console.error(error, errorInfo);
    const user = this.props.platform.getter(userAtom);
    if (user) {
      handleError(user, error, 'ConsoleErrorBoundary', 0, 0);
    }
  }

  render() {
    if (this.state.hasError) {
      const { platform, name } = this.props;
      platform.showError(
        `Error rending ${name}. Check console log for more details!`,
      );
      console.error(this.state.message);
      return null;
    }
    return this.props.children ?? null;
  }
}
